import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

function todayYMD() {
  return new Date().toISOString().slice(0, 10);
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function cleanStr(v) {
  return String(v ?? "").trim();
}

function pick(obj, keys, fallback = "") {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
}

function toArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function getEmpCode(emp) {
  return emp?.emp_code || emp?.empCode || emp?.code || "";
}

function getEmpName(emp) {
  return emp?.emp_name || emp?.empName || emp?.name || "";
}

function formatDMY(val) {
  if (!val) return "";
  const s = String(val).trim();
  const onlyDate = s.slice(0, 10);

  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(onlyDate);
  if (ymd) return `${ymd[3]}/${ymd[2]}/${ymd[1]}`;

  const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (dmy) return s;

  return s;
}

// convert any yyyy-mm-dd found inside text to dd/mm/yyyy
function replaceYMDInsideText(text) {
  return String(text || "").replace(
    /\b(\d{4})-(\d{2})-(\d{2})\b/g,
    (_, y, m, d) => `${d}/${m}/${y}`
  );
}

function formatSourceLabel(s) {
  const sourceType = pick(s, ["sourceType", "source_type"], "");
  const sourceLineId = pick(s, ["sourceLineId", "source_line_id"], "");
  const productionDate = formatDMY(
    pick(s, ["productionDate", "production_date"], "")
  );
  const itemCode = pick(s, ["itemCode", "item_code"], "");
  const remainingKg = num(pick(s, ["remainingKg", "remaining_kg"], 0)).toFixed(2);

  // if proper fields exist, build our own label
  if (sourceLineId || productionDate || itemCode) {
    if (sourceType === "C1" || sourceType === "C2") {
      return `${sourceLineId} | ${productionDate} | ${itemCode} | Rem: ${remainingKg} KG`;
    }
    return `${sourceType} | ${sourceLineId} | ${productionDate} | ${itemCode} | Rem: ${remainingKg} KG`;
  }

  // fallback: fix any API label text
  return replaceYMDInsideText(pick(s, ["label"], ""));
}

export default function RecyclingAdd() {
  const navigate = useNavigate();

  const [date, setDate] = useState(todayYMD());
  const [receivedBy, setReceivedBy] = useState("");

  const [items, setItems] = useState([]);
  const [sources, setSources] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [rows, setRows] = useState([
    {
      sourceType: "",
      sourceLineId: "",
      itemCode: "",
      itemName: "",
      remainingKg: 0,
      machineNo: "",
      operatorName: "",
      timeHour: "",
      resultKg: "",
      receiveDate: "",
    },
  ]);

  const itemNameByCode = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const code = pick(it, ["item_code", "itemCode"], "");
      const name = pick(it, ["item_name", "itemName"], "");
      if (code) map.set(code, name);
    });
    return map;
  }, [items]);

  const sourceById = useMemo(() => {
    const map = new Map();
    sources.forEach((s) => {
      const sourceType = pick(s, ["sourceType", "source_type"], "");
      const sourceLineId = pick(s, ["sourceLineId", "source_line_id"], "");
      map.set(`${sourceType}_${String(sourceLineId)}`, s);
    });
    return map;
  }, [sources]);

  useEffect(() => {
    (async () => {
      try {
        const [iRes, c1Res, c2Res, empRes] = await Promise.all([
          api.get(`/api/items?type=recycle`),
          api.get(`/api/recycling/sources/crush1`),
          api.get(`/api/recycling/sources/crush2`),
          api.get(`/api/employees`),
        ]);

        setItems(toArray(iRes.data));
        setSources([...toArray(c1Res.data), ...toArray(c2Res.data)]);
        setEmployees(toArray(empRes.data));
      } catch (e) {
        alert(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            e.message ||
            "Load failed"
        );
      }
    })();
  }, []);

  const sourceOptions = useMemo(() => {
    return sources.map((s) => {
      const sourceType = pick(s, ["sourceType", "source_type"], "");
      const sourceLineId = pick(s, ["sourceLineId", "source_line_id"], "");
      const sourceKey = `${sourceType}_${sourceLineId}`;
      const label = formatSourceLabel(s);

      return {
        value: sourceKey,
        label,
        searchText: `${label} ${pick(s, ["itemCode", "item_code"], "")} ${
          pick(s, ["itemName", "item_name"], "")
        } ${pick(s, ["machineNo", "machine_no"], "")} ${sourceType}`.toLowerCase(),
      };
    });
  }, [sources]);

  const employeeOptions = useMemo(() => {
    return employees
      .map((emp) => {
        const empCode = getEmpCode(emp);
        const empName = getEmpName(emp);

        if (!empName) return null;

        return {
          value: empName,
          label: empName,
          searchText: `${empName} ${empCode}`.toLowerCase(),
        };
      })
      .filter(Boolean);
  }, [employees]);

  const grandTotalKg = useMemo(
    () => rows.reduce((s, r) => s + num(r.resultKg), 0),
    [rows]
  );

  function updateRow(i, patch) {
    setRows((prev) => {
      const copy = [...prev];
      copy[i] = { ...copy[i], ...patch };
      return copy;
    });
  }

  function onSelectSource(i, value) {
    setRows((prev) => {
      const copy = [...prev];
      const src = sourceById.get(value);

      if (!src) {
        copy[i] = {
          ...copy[i],
          sourceType: "",
          sourceLineId: "",
          itemCode: "",
          itemName: "",
          remainingKg: 0,
          machineNo: "",
          resultKg: "",
        };
        return copy;
      }

      const sourceType = pick(src, ["sourceType", "source_type"], "");
      const sourceLineId = pick(src, ["sourceLineId", "source_line_id"], "");
      const code = pick(src, ["itemCode", "item_code"], "");
      const rem = num(pick(src, ["remainingKg", "remaining_kg"], 0));
      const machineNo = pick(src, ["machineNo", "machine_no"], "");
      const itemName =
        itemNameByCode.get(code) || pick(src, ["itemName", "item_name"], "");
      const oldResult = num(copy[i].resultKg);

      copy[i] = {
        ...copy[i],
        sourceType,
        sourceLineId: String(sourceLineId),
        itemCode: code,
        itemName,
        remainingKg: rem,
        machineNo,
        resultKg: oldResult > rem ? "" : copy[i].resultKg,
      };

      return copy;
    });
  }

  function addRow() {
    setRows((p) => [
      ...p,
      {
        sourceType: "",
        sourceLineId: "",
        itemCode: "",
        itemName: "",
        remainingKg: 0,
        machineNo: "",
        operatorName: "",
        timeHour: "",
        resultKg: "",
        receiveDate: "",
      },
    ]);
  }

  function removeRow(i) {
    setRows((p) => (p.length === 1 ? p : p.filter((_, x) => x !== i)));
  }

  async function save() {
    try {
      if (!date) return alert("Date required");
      if (!cleanStr(receivedBy)) return alert("Received By required");

      for (const r of rows) {
        if (!r.sourceLineId) continue;
        if (!cleanStr(r.operatorName)) return alert("Operator Name required");
        if (num(r.resultKg) <= 0) return alert("Result KG must be > 0");
        if (num(r.resultKg) > num(r.remainingKg)) {
          return alert("Result KG cannot be greater than Remaining KG");
        }
      }

      const lines = rows
        .map((r) => ({
          sourceType: cleanStr(r.sourceType),
          sourceLineId: Number(r.sourceLineId || 0),
          machineNo: cleanStr(r.machineNo),
          operatorName: cleanStr(r.operatorName),
          timeHour: cleanStr(r.timeHour),
          itemCode: cleanStr(r.itemCode),
          itemName: cleanStr(r.itemName),
          remainingKg: num(r.remainingKg),
          resultKg: num(r.resultKg),
          receiveDate: cleanStr(r.receiveDate) ? cleanStr(r.receiveDate) : null,
        }))
        .filter((x) => x.sourceLineId && x.resultKg > 0);

      if (!lines.length) {
        return alert("Select at least one Source and enter KG");
      }

      const res = await api.post(`/api/recycling`, {
        productionDate: date,
        receivedBy: cleanStr(receivedBy),
        lines,
      });

      alert("Saved! Entry ID: " + (res.data?.headerId ?? ""));
      navigate("/recycling");
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e.message ||
          "Save failed"
      );
    }
  }

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "36px",
      height: "36px",
      border: "1px solid #ced4da",
      boxShadow: "none",
      borderRadius: "0.375rem",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "36px",
      padding: "0 8px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
      padding: "0px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "36px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      marginTop: "6px",
      marginBottom: "6px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontSize: "14px",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#212529",
    }),
  };

  const filterSelectOption = (option, inputValue) => {
    const text = `${option.label} ${option.data.searchText || ""}`.toLowerCase();
    return text.includes(String(inputValue).toLowerCase());
  };

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2 className="mb-0">Daily Recycling Entry</h2>

        <div className="d-flex gap-2">
          <button className="btn btn-dark" onClick={save}>
            Save
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate("/recycling")}
          >
            Close
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Date</label>
              <input
                type="date"
                className="form-control"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>

            <div className="col-md-5">
              <label className="form-label">Received By</label>
              <Select
                options={employeeOptions}
                value={
                  employeeOptions.find((opt) => opt.value === receivedBy) || null
                }
                onChange={(selected) =>
                  setReceivedBy(selected ? selected.value : "")
                }
                placeholder="-- Select Employee --"
                isClearable
                isSearchable
                styles={customSelectStyles}
                noOptionsMessage={() => "No employee found"}
                filterOption={filterSelectOption}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-bordered align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 60 }}>SN</th>
                <th style={{ width: 360 }}>Source (Crush 1 + Crush 2 Remaining)</th>
                <th style={{ width: 150 }}>Machine</th>
                <th style={{ width: 220 }}>Operator Name</th>
                <th style={{ width: 150 }}>Time / Hour</th>
                <th style={{ width: 120 }}>Item Code</th>
                <th style={{ minWidth: 220 }}>Item Name</th>
                <th style={{ width: 140 }}>Remaining KG</th>
                <th style={{ width: 140 }}>Result / KG</th>
                <th style={{ width: 170 }}>Receive Date</th>
                <th style={{ width: 180 }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="text-center">{i + 1}</td>

                  <td className="align-middle">
                    <Select
                      options={sourceOptions}
                      value={
                        r.sourceType && r.sourceLineId
                          ? sourceOptions.find(
                              (opt) =>
                                opt.value === `${r.sourceType}_${r.sourceLineId}`
                            ) || null
                          : null
                      }
                      onChange={(selected) =>
                        onSelectSource(i, selected ? selected.value : "")
                      }
                      placeholder="-- Select --"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No source found"}
                      filterOption={filterSelectOption}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={r.machineNo}
                      readOnly
                      placeholder="Auto"
                    />
                  </td>

                  <td className="align-middle">
                    <Select
                      options={employeeOptions}
                      value={
                        employeeOptions.find(
                          (opt) => opt.value === r.operatorName
                        ) || null
                      }
                      onChange={(selected) =>
                        updateRow(i, {
                          operatorName: selected ? selected.value : "",
                        })
                      }
                      placeholder="-- Select --"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No employee found"}
                      filterOption={filterSelectOption}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={r.timeHour}
                      onChange={(e) =>
                        updateRow(i, { timeHour: e.target.value })
                      }
                      placeholder="e.g. 11:30 / 8"
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={r.itemCode}
                      readOnly
                      placeholder="Auto"
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={r.itemName}
                      readOnly
                      placeholder="Auto"
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm text-end"
                      value={num(r.remainingKg).toFixed(2)}
                      readOnly
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm text-end"
                      value={r.resultKg}
                      onChange={(e) => updateRow(i, { resultKg: e.target.value })}
                      placeholder="e.g. 200"
                    />
                  </td>

                  <td>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={r.receiveDate}
                      onChange={(e) =>
                        updateRow(i, { receiveDate: e.target.value })
                      }
                    />
                  </td>

                  <td>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeRow(i)}
                      >
                        Remove
                      </button>
                      <button
                        className="btn btn-sm btn-outline-dark"
                        onClick={addRow}
                      >
                        + Add
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card-footer d-flex justify-content-between">
          <b>Total Records: {rows.length}</b>
          <b>Grand Total KG: {grandTotalKg.toFixed(2)}</b>
        </div>
      </div>
    </div>
  );
}